import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../supabaseClient";
import { useSeason } from "../context/SeasonContext";
import PlayerStatsModal from "../components/PlayerStatsModal";
import PlayerComparisonPanel from "../components/PlayerComparisonPanel";
import StandingsSeasonSelector from "../components/StandingsSeasonSelector";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";
import JerseyNumber from "../utils/JerseyNumber";
import { GitCompare } from "lucide-react";

const STAT_CATEGORIES = [
  { key: "ppg", label: "Points", icon: "🏀" },
  { key: "rpg", label: "Rebounds", icon: "🤚" },
  { key: "apg", label: "Assists", icon: "🪄" },
  { key: "tpmpg", label: "3PM", icon: "🎯" },
  { key: "twpmpg", label: "2PM", icon: "⛹️" },
  { key: "ftmpg", label: "FTM", icon: "📏" },
  { key: "pfpg", label: "Fouls", icon: "🔔" },
];

const Stats = () => {
  const { selectedSeason, seasons } = useSeason();
  const [sortBy, setSortBy] = useState("ppg");

  const [statsTab, setStatsTab] = useState("regular"); // 'regular' | 'playoff'
  const [rawRows, setRawRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const debounceTimer = useRef(null);

  // Modal state
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerGameStats, setPlayerGameStats] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);

  // Comparison panel state
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareQueue, setCompareQueue] = useState([]); // array of player objects (max 4)

  useEffect(() => {
    if (selectedSeason?.status === "playoffs") {
      setStatsTab("playoff");
    } else {
      setStatsTab("regular");
    }
  }, [selectedSeason]);

  const loadStats = useCallback(async (seasonId) => {
    if (!seasonId) return;
    setLoading(true);
    const PAGE = 1000;
    let data = [];
    let from = 0;

    while (true) {
      const { data: chunk, error } = await supabase
        .from("game_stats")
        .select(
          `
                    player_id, points, rebounds, assists, personal_fouls, technical_fouls,
                    three_pm, two_pm, ft_made,
                    game:games!game_stats_game_id_fkey(id, game_type, season_id, status),
                    player:players!game_stats_player_id_fkey(
                        id, name, jersey_number, team_id,
                        team:teams!players_team_id_fkey(id, name, archived_at, primary_color, secondary_color, gradient)
                    )
                `,
        )
        .eq("game.season_id", seasonId)
        .range(from, from + PAGE - 1);

      if (error) {
        setLoading(false);
        return;
      }

      data = data.concat(chunk ?? []);
      if (!chunk || chunk.length < PAGE) break; // last page reached
      from += PAGE;
      if (from > 100000) break;
    }

    console.log("total game_stats rows returned:", data.length);
    // Keep only this season's finished rows; aggregation happens per-tab in useMemo below
    const finished = (data ?? []).filter(
      (row) =>
        row.game &&
        row.game.season_id === seasonId &&
        row.game.status === "finished" &&
        row.player &&
        row.player.team_id,
    );
    setRawRows(finished);
    setLoading(false);
  }, []);

  const rows = React.useMemo(() => {
    const playerMap = {};
    for (const row of rawRows) {
      if (row.game.game_type !== statsTab) continue; // ← the tab filter

      // ── DNP Filter: Skip games where all stats are zero ──
      const isDNP =
        row.points === 0 &&
        row.rebounds === 0 &&
        row.assists === 0 &&
        row.three_pm === 0 &&
        row.two_pm === 0 &&
        row.ft_made === 0 &&
        (row.personal_fouls ?? 0) === 0 &&
        (row.technical_fouls ?? 0) === 0;
      if (isDNP) continue;

      const pid = row.player_id;
      if (!playerMap[pid]) {
        playerMap[pid] = {
          id: pid,
          player_id: pid,
          name: row.player.name,
          jersey: row.player.jersey_number,
          team: row.player.team?.name,
          team_id: row.player.team_id,
          team_primary_color: row.player.team?.primary_color,
          team_secondary_color: row.player.team?.secondary_color,
          team_gradient: row.player.team?.gradient,
          _games: new Set(),
          pts: 0,
          reb: 0,
          ast: 0,
          pf: 0,
          t3: 0,
          t2: 0,
          ft: 0,
        };
      }
      const p = playerMap[pid];
      p._games.add(row.game.id);
      p.pts += row.points;
      p.reb += row.rebounds;
      p.ast += row.assists;
      p.pf += row.personal_fouls;
      p.t3 += row.three_pm;
      p.t2 += row.two_pm;
      p.ft += row.ft_made;
    }
    return Object.values(playerMap).map((p) => {
      const gp = p._games.size;
      return {
        ...p,
        gp,
        ppg: p.pts / gp,
        rpg: p.reb / gp,
        apg: p.ast / gp,
        pfpg: p.pf / gp,
        tpmpg: p.t3 / gp,
        twpmpg: p.t2 / gp,
        ftmpg: p.ft / gp,
      };
    });
  }, [rawRows, statsTab]);

  const teamOptions = React.useMemo(() => {
    const teams = Array.from(
      new Set(rows.map((p) => JSON.stringify({ id: p.team_id, name: p.team }))),
    )
      .map((s) => JSON.parse(s))
      .sort((a, b) => a.name.localeCompare(b.name));
    return teams;
  }, [rows]);

  // Fetch player's game-by-game stats for modal
  const loadPlayerGameStats = useCallback(async (playerId, seasonId) => {
    setModalLoading(true);
    const { data, error } = await supabase
      .from("game_stats")
      .select(
        `
                points, rebounds, assists, personal_fouls, technical_fouls,
                three_pm, two_pm, ft_made,
                game:games!game_stats_game_id_fkey(
                    id, game_date, game_type, playoff_round, team1_id, team2_id, team1_score, team2_score,
                    team1:teams!games_team1_id_fkey(id, name),
                    team2:teams!games_team2_id_fkey(id, name)
                )
            `,
      )
      .eq("player_id", playerId)
      .eq("game.season_id", seasonId);

    if (!error && data) {
      setPlayerGameStats(data);
    }
    setModalLoading(false);
  }, []);

  const handlePlayerClick = (player) => {
    setSelectedPlayer(player);
    loadPlayerGameStats(player.id, selectedSeason?.id);
  };

  useEffect(() => {
    loadStats(selectedSeason?.id);
  }, [selectedSeason?.id, loadStats]);

  // Debounce: update debouncedSearch 400ms after user stops typing
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchInput(val);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setDebouncedSearch(val);
    }, 400);
  };

  // Instant search on Enter or button click
  const applySearchNow = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setDebouncedSearch(searchInput);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") applySearchNow();
  };

  // Clear search
  const clearSearch = () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setSearchInput("");
    setDebouncedSearch("");
  };

  // Toggle a player in/out of the comparison queue
  const toggleCompare = (player, e) => {
    e.stopPropagation(); // don't open the stats modal
    setCompareQueue((prev) => {
      const already = prev.find((p) => p.id === player.id);
      if (already) return prev.filter((p) => p.id !== player.id);
      if (prev.length >= 4) return prev; // max 4
      return [...prev, player];
    });
  };

  const filtered = rows.filter((p) => {
    const teamMatch =
      teamFilter === "all" || String(p.team_id) === String(teamFilter);
    const nameMatch =
      debouncedSearch.trim() === "" ||
      p.name.toLowerCase().includes(debouncedSearch.trim().toLowerCase());
    return teamMatch && nameMatch;
  });
  const sorted = [...filtered].sort((a, b) => b[sortBy] - a[sortBy]);
  const leaders = [...rows].sort((a, b) => b.ppg - a.ppg).slice(0, 3);

  // Flat list of all players for the comparison panel's search dropdown
  const allPlayersForCompare = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const p of rows) {
      if (!seen.has(p.id)) {
        seen.add(p.id);
        out.push(p);
      }
    }
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  // Medal labels for top 3
  const medals = ["🥇", "🥈", "🥉"];

  const compareCount = compareQueue.length;

  return (
    <div className="space-y-6">
      {/* Comparison panel (rendered outside normal flow so it overlays) */}
      <PlayerComparisonPanel
        isOpen={compareOpen}
        onClose={() => setCompareOpen(false)}
        initialPlayers={compareQueue}
        allPlayers={allPlayersForCompare}
        seasons={seasons || []}
        defaultSeasonId={selectedSeason?.id || null}
      />
      {/* Season Selector */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden border border-gray-100 dark:border-gray-800">
        <StandingsSeasonSelector />
      </div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tight uppercase italic">
            Player Stats
          </h1>
          <p className="text-gray-500 font-medium">League Leaders & Averages</p>
        </div>

        {/* Compare button — always visible, shows queue count */}
        <button
          onClick={() => setCompareOpen(true)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition shrink-0 shadow-sm ${
            compareCount > 0
              ? "bg-orange-600 hover:bg-orange-700 text-white"
              : "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-orange-400 hover:text-orange-600"
          }`}
        >
          <GitCompare size={16} />
          Compare
          {compareCount > 0 && (
            <span className="bg-white/25 text-white text-xs font-black px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
              {compareCount}
            </span>
          )}
        </button>
      </div>

      {/* Top 3 Leaders — team-colored cards */}
      {!loading && rows.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {leaders.map((player, idx) => {
            const teamObj = {
              primary_color: player.team_primary_color,
              secondary_color: player.team_secondary_color,
              gradient: player.team_gradient,
            };
            const ts = getTeamStyle(teamObj);
            const isDark = isDarkModeActive();
            const cardTextColor = getTextColorForAccent(ts.primaryHex, isDark);
            const isLightText = cardTextColor === "#ffffff";
            return (
              <button
                key={idx}
                onClick={() => handlePlayerClick(player)}
                className="relative overflow-hidden rounded-2xl p-5 shadow-xl border border-white/10 text-left hover:shadow-2xl transition-shadow"
                style={{ ...ts.headerStyle, color: cardTextColor }}
              >
                {/* Watermark basketball */}
                <div className="absolute -right-4 -bottom-4 text-7xl opacity-10 grayscale select-none">
                  🏀
                </div>

                {/* Medal + rank */}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{medals[idx]}</span>
                  <span
                    className="font-black italic text-sm uppercase tracking-widest"
                    style={{
                      color: isLightText
                        ? "rgba(255,255,255,0.8)"
                        : "rgba(0,0,0,0.6)",
                    }}
                  >
                    #{idx + 1} Scorer
                  </span>
                </div>

                <h3
                  className="text-2xl font-bold truncate leading-tight"
                  style={{ color: cardTextColor }}
                >
                  {player.name}
                </h3>
                <p
                  className="font-medium mb-4 text-sm"
                  style={{
                    color: isLightText
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.55)",
                  }}
                >
                  {player.team} · #{player.jersey}
                </p>

                {/* Score display */}
                <div className="flex items-baseline gap-2">
                  <span
                    className="text-4xl font-black"
                    style={{ color: cardTextColor }}
                  >
                    {player.ppg.toFixed(1)}
                  </span>
                  <span
                    className="font-bold uppercase text-sm tracking-widest"
                    style={{
                      color: isLightText
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.55)",
                    }}
                  >
                    PPG
                  </span>
                </div>

                {/* Secondary stats strip */}
                <div
                  className="flex gap-4 mt-3 pt-3 text-xs font-semibold"
                  style={{
                    borderTop: `1px solid ${isLightText ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)"}`,
                    color: isLightText
                      ? "rgba(255,255,255,0.6)"
                      : "rgba(0,0,0,0.5)",
                  }}
                >
                  <span>
                    {player.rpg.toFixed(1)}{" "}
                    <span
                      style={{
                        color: isLightText
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.35)",
                      }}
                    >
                      RPG
                    </span>
                  </span>
                  <span>
                    {player.apg.toFixed(1)}{" "}
                    <span
                      style={{
                        color: isLightText
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.35)",
                      }}
                    >
                      APG
                    </span>
                  </span>
                  <span>
                    {player.tpmpg.toFixed(1)}{" "}
                    <span
                      style={{
                        color: isLightText
                          ? "rgba(255,255,255,0.4)"
                          : "rgba(0,0,0,0.35)",
                      }}
                    >
                      3PG
                    </span>
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Regular / Playoff tabs */}
      <div className="flex border-b dark:border-gray-700">
        <button
          onClick={() => setStatsTab("regular")}
          className={`px-8 py-3 font-semibold border-b-2 transition-all ${
            statsTab === "regular"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Regular Season
        </button>
        <button
          onClick={() => setStatsTab("playoff")}
          className={`px-8 py-3 font-semibold border-b-2 transition-all ${
            statsTab === "playoff"
              ? "border-orange-600 text-orange-600"
              : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
          }`}
        >
          Playoffs
        </button>
      </div>

      {/* Compare queue pill strip */}
      {compareCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide shrink-0">
            Comparing:
          </span>
          {compareQueue.map((p) => {
            const teamObj = {
              primary_color: p.team_primary_color,
              secondary_color: p.team_secondary_color,
              gradient: p.team_gradient,
            };
            const ts = getTeamStyle(teamObj);
            return (
              <span
                key={p.id}
                className="flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-bold text-white shadow-sm"
                style={{ background: ts.primaryHex }}
              >
                {p.name}
                <button
                  onClick={(e) => toggleCompare(p, e)}
                  className="hover:bg-white/20 rounded-full p-0.5 transition"
                  aria-label={"Remove " + p.name + " from comparison"}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </span>
            );
          })}
          <button
            onClick={() => setCompareQueue([])}
            className="text-xs text-gray-400 hover:text-red-500 dark:hover:text-red-400 font-semibold transition ml-1"
          >
            Clear all
          </button>
          <button
            onClick={() => setCompareOpen(true)}
            className="ml-auto text-xs font-bold text-orange-600 dark:text-orange-400 hover:underline shrink-0"
          >
            Open comparison →
          </button>
        </div>
      )}

      {/* Filter Bar */}
      <div className="bg-white dark:bg-gray-900 p-2 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 flex flex-wrap gap-2">
        <select
          onChange={(e) => setTeamFilter(e.target.value)}
          className="px-4 py-2 bg-gray-50 dark:bg-gray-800 rounded-xl font-bold text-sm outline-none border-none text-gray-700 dark:text-gray-300"
        >
          <option value="all">All Teams</option>
          {teamOptions.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
        <div className="h-8 w-[1px] bg-gray-200 dark:bg-gray-700 self-center hidden md:block" />
        <div className="flex gap-1 overflow-x-auto pb-2 md:pb-0">
          {STAT_CATEGORIES.map((cat) => (
            <button
              key={cat.key}
              onClick={() => setSortBy(cat.key)}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase transition-all whitespace-nowrap
                                ${
                                  sortBy === cat.key
                                    ? "bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900 shadow-sm"
                                    : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
                                }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {/* Search Bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
              />
            </svg>
          </span>
          <input
            type="text"
            value={searchInput}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            placeholder="Search by player name..."
            className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-800 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 transition"
          />
          {searchInput && (
            <button
              onClick={clearSearch}
              aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={applySearchNow}
          className="px-4 py-2.5 rounded-xl bg-orange-600 hover:bg-orange-700 text-white text-sm font-bold transition shrink-0"
        >
          Search
        </button>
      </div>

      {/* Stats Table */}
      <div className="bg-white dark:bg-gray-900 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50">
                <th className="p-4 text-xs font-black uppercase text-gray-400 sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">
                  Player
                </th>
                <th className="p-4 text-xs font-black uppercase text-gray-400 text-center">
                  GP
                </th>
                {STAT_CATEGORIES.map((cat) => (
                  <th
                    key={cat.key}
                    className={`p-4 text-xs font-black uppercase text-center transition-colors
                                        ${sortBy === cat.key ? "text-gray-900 dark:text-white" : "text-gray-400"}`}
                  >
                    {cat.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sorted.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan={2 + STAT_CATEGORIES.length}
                    className="py-16 text-center text-gray-400 dark:text-gray-500 font-semibold text-sm"
                  >
                    {debouncedSearch.trim()
                      ? `No players found matching "${debouncedSearch.trim()}"`
                      : "No stats available."}
                  </td>
                </tr>
              )}
              {sorted.map((p, i) => {
                const teamObj = {
                  primary_color: p.team_primary_color,
                  secondary_color: p.team_secondary_color,
                  gradient: p.team_gradient,
                };
                const ts = getTeamStyle(teamObj);
                const isInCompare = compareQueue.some((c) => c.id === p.id);
                return (
                  <tr
                    key={i}
                    onClick={() => handlePlayerClick(p)}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors group cursor-pointer ${
                      isInCompare ? "bg-orange-50/60 dark:bg-orange-900/10" : ""
                    }`}
                  >
                    <td
                      className={`p-4 sticky left-0 z-10 ${
                        isInCompare
                          ? "bg-orange-50/60 dark:bg-orange-900/10 group-hover:bg-orange-100/60 dark:group-hover:bg-orange-900/20"
                          : "bg-white dark:bg-gray-900 group-hover:bg-gray-50 dark:group-hover:bg-gray-800/40"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {/* Compare toggle button */}
                        <button
                          onClick={(e) => toggleCompare(p, e)}
                          title={
                            isInCompare
                              ? "Remove from comparison"
                              : compareCount >= 4
                                ? "Max 4 players"
                                : "Add to comparison"
                          }
                          disabled={!isInCompare && compareCount >= 4}
                          className={`shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition ${
                            isInCompare
                              ? "bg-orange-500 border-orange-500 text-white"
                              : compareCount >= 4
                                ? "border-gray-200 dark:border-gray-700 opacity-30 cursor-not-allowed"
                                : "border-gray-300 dark:border-gray-600 hover:border-orange-400 text-transparent hover:text-orange-400"
                          }`}
                          aria-label={
                            isInCompare
                              ? "Remove from comparison"
                              : "Add to comparison"
                          }
                        >
                          {isInCompare ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={3}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="w-3 h-3"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2.5}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                          )}
                        </button>

                        {/* Team color bar */}
                        <div
                          className="w-1 self-stretch rounded-full shrink-0 min-h-[32px]"
                          style={ts.accentStyle}
                        />
                        <div className="flex flex-col">
                          <span className="font-bold text-gray-900 dark:text-white leading-none">
                            {p.name}
                          </span>
                          <span className="text-[10px] font-bold text-gray-400 uppercase mt-1 flex items-center gap-1">
                            <JerseyNumber
                              number={p.jersey}
                              color={ts.primaryHex}
                              size="xs"
                              mode="inline"
                            />
                            · {p.team}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-center font-mono font-bold text-gray-500">
                      {p.gp}
                    </td>
                    {STAT_CATEGORIES.map((cat) => (
                      <td
                        key={cat.key}
                        className={`p-4 text-center font-mono font-bold transition-colors
                                                    ${
                                                      sortBy === cat.key
                                                        ? "text-gray-900 dark:text-white bg-gray-50/80 dark:bg-gray-800/60"
                                                        : "text-gray-700 dark:text-gray-300"
                                                    }`}
                      >
                        {p[cat.key].toFixed(1)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {selectedPlayer && (
        <PlayerStatsModal
          player={selectedPlayer}
          gameStats={playerGameStats.filter(
            (row) => row.game?.game_type === statsTab,
          )}
          modalLoading={modalLoading}
          teamStyle={getTeamStyle({
            primary_color: selectedPlayer.team_primary_color,
            secondary_color: selectedPlayer.team_secondary_color,
            gradient: selectedPlayer.team_gradient,
          })}
          onClose={() => {
            setSelectedPlayer(null);
            setPlayerGameStats([]);
          }}
        />
      )}
    </div>
  );
};

export default Stats;
